// Utility for checking component visibility and lazy-loading
// Components can use this to only load data when they're actually visible

/**
 * Check if an element is currently visible in the DOM
 * @param {HTMLElement} element - The element to check
 * @returns {boolean} - True if the element is visible
 */
export function isVisible(element) {
  if (!element) return false;

  // Check if element has display: none or is not in the layout
  return element.offsetParent !== null &&
         !element.style.display?.includes('none') &&
         element.offsetWidth > 0 &&
         element.offsetHeight > 0;
}

/**
 * Set up an IntersectionObserver to call a callback when an element becomes visible
 * @param {HTMLElement} element - The element to observe
 * @param {Function} callback - Function to call when element becomes visible
 * @param {Object} options - IntersectionObserver options
 * @returns {IntersectionObserver} - The observer instance (call .disconnect() when done)
 */
export function observeVisibility(element, callback, options = {}) {
  if (!element || typeof callback !== 'function') {
    return null;
  }

  // If already visible, call callback immediately
  if (isVisible(element)) {
    callback();
    return null;
  }

  const observerOptions = {
    threshold: 0.1, // Trigger when 10% visible
    rootMargin: '50px', // Start loading slightly before fully visible
    ...options
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && isVisible(element)) {
        callback();
        observer.disconnect(); // Only trigger once
      }
    });
  }, observerOptions);

  observer.observe(element);
  return observer;
}

